import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SpeechToTextInput.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faStopCircle, faMicrophoneSlash } from '@fortawesome/free-solid-svg-icons';

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const SpeechToTextInput = ({
  onChange = () => {}, // Default onChange function (noop)
  onSubmit = () => {}, // Function to handle form submission
  isReadOnly = false,  // Add isReadOnly prop to disable speech-to-text functionality
}) => {
  const [isListening, setIsListening] = useState(false);
  const [micIcon, setMicIcon] = useState(faMicrophone); // State to toggle mic icon
  const [recognition, setRecognition] = useState(null);
  const transcriptRef = useRef(''); // Ref to preserve typed and spoken text

  // Memoize stopListening to avoid unnecessary re-renders
  const stopListening = useCallback(() => {
    if (recognition && isListening) {
      recognition.stop(); // Stop recognition if it's running
      setIsListening(false); // Update state
      setMicIcon(faMicrophoneSlash); // Switch to Microphone Slash icon when stopped manually

      // Reset to original mic icon after a few seconds
      setTimeout(() => {
        setMicIcon(faMicrophone);
      }, 3000);
    }
  }, [recognition, isListening]);

  // Memoize the commands object with only clear and submit
  const commands = useMemo(() => ({
    clear: () => {
      transcriptRef.current = ''; 
    },
    submit: () => {
      onSubmit(); // Trigger form submission
    }
  }), [onSubmit]);

  // Memoize checkCommand to avoid unnecessary re-renders
  const checkCommand = useCallback((spokenText) => {
    const command = spokenText.toLowerCase().trim();
    if (commands[command]) {
      commands[command](); // Trigger the corresponding action
    }
  }, [commands]);

  // Handle the automatic stopping of speech recognition
  const handleRecognitionEnd = useCallback(() => {
    setIsListening(false); // Ensure isListening is updated
    setMicIcon(faMicrophoneSlash);

    // Reset to original mic icon after a few seconds
    setTimeout(() => {
      setMicIcon(faMicrophone);
    }, 3000);
  }, []);

  // Initialize SpeechRecognition once on mount
  useEffect(() => {
    if (SpeechRecognition && !recognition) {
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true; // Continuous mode
      recognitionInstance.interimResults = true; // Capture partial results
      recognitionInstance.lang = 'en-US'; // Set language

      recognitionInstance.onresult = (event) => {
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            final += event.results[i][0].transcript; // Final recognized speech
          }
        }

        if (final) {
          const updatedTranscript = transcriptRef.current + ' ' + final;
          transcriptRef.current = updatedTranscript; // Update ref with the latest transcript

          // Call onChange to propagate updated transcript back to parent
          if (typeof onChange === 'function') {
            onChange(final.trim()); // Send only new speech transcript
          }

          // Check if the final transcript matches a command
          checkCommand(final);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        stopListening(); // Stop listening in case of an error
      };

      recognitionInstance.onend = handleRecognitionEnd; // Handle automatic stopping

      setRecognition(recognitionInstance); // Only set once
    }
  }, [recognition, stopListening, handleRecognitionEnd, onChange, checkCommand]);

  // Start the speech recognition
  const startListening = () => {
    if (recognition && !isListening && !isReadOnly) { // Prevent starting if read-only
      recognition.start();
      setIsListening(true);
      setMicIcon(faMicrophone); // Set back to mic icon when starting again
    }
  };

  return (
    <div className="speech-to-text-wrapper">
      <div className="input-container-null">
        <button
          type="button"
          onClick={startListening}
          className={`mic-button ${isListening ? 'listening' : ''} ${isReadOnly ? 'disabled-mic' : ''}`} // Add disabled-mic class when readonly
          title={isReadOnly ? "Speech input disabled" : "Start Listening"}
          disabled={isReadOnly} // Disable button if the input is read-only
        >
          <FontAwesomeIcon icon={micIcon} />
        </button>
        <button
          type="button"
          onClick={stopListening}
          className={`mic-button stop-button ${isReadOnly ? 'disabled-mic' : ''}`} // Add disabled-mic class for read-only state
          title={isReadOnly ? "Speech input disabled" : "Stop Listening"}
          disabled={isReadOnly} // Disable button if the input is read-only
        >
          <FontAwesomeIcon icon={faStopCircle} />
        </button>

      </div>
    </div>
  );
};

export default SpeechToTextInput;
